import axios from 'axios';
import {
    SubEvent, CreateSubEventDTO, UpdateSubEventDTO,
    SubEventType, CreateTypeDTO, ConflictCheckDTO,
    CheckInEntry, AttendanceEntry,
    KeyHandout,
    Defect,
    KeyDashboardItem,
    VenuePrice,
    VenueExtra,
    CalculationResult
} from '../types';

const API_BASE = '/api/retreat';

// Create an axios instance with default config
const apiClient = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add response interceptor for better error handling
apiClient.interceptors.response.use(
    response => response,
    error => {
        // Extract error message from response if available
        const message = error.response?.data?.error || error.message || 'An error occurred';
        console.error('API Error:', message, error.response?.data);
        return Promise.reject(new Error(message));
    }
);

export interface RetreatEvent {
    id: number;
    name: string;
    location: string | null;
    startDate: string;
    endDate: string;
    earlyBirdDeadline: string | null;
    earlyBirdDays: number | null;
    bedLinensPrice: number | null;
    roomDeduction: number | null;
    breakfastDeduction: number | null;
    lunchDeduction: number | null;
    dinnerDeduction: number | null;
    dietaryOptions: string[] | null;
    isActive: boolean;
    publicId: string;
    createdAt: string;
    updatedAt: string;
}

export interface RetreatParticipant {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    birthDate: string | null;
    gender: 'm' | 'f' | null;
    isDiscounted: boolean;
    phone: string | null;
    dietaryOption?: string;
}

export interface RetreatParticipation {
    id: number;
    eventId: number;
    participantId: number;
    familyId: number | null;
    coupleId: number | null;
    needsBedLinens: boolean;
    needsBed?: boolean;
    availableSeats: number;
    rideStatus: 'SEARCHING' | 'OFFERING' | 'NONE';
    customPriceOverride: number | null;
    calculatedPrice: number | null;
    hasPaid: boolean;
    status: 'PENDING' | 'CONFIRMED' | 'CANCELLED';
    notes: string | null;
    registeredAt: string;
    dietaryOption?: string;
}

export interface RegistrationWithDetails {
    participation: RetreatParticipation;
    participant: RetreatParticipant | null;
    event: RetreatEvent | null;
    family: { id: number; name: string } | null;
    couple: { id: number } | null;
}

// Events API
export async function getEvents(): Promise<RetreatEvent[]> {
    const response = await apiClient.get('/events');
    return response.data;
}

export async function getEvent(id: number): Promise<RetreatEvent & { prices: unknown[] }> {
    const response = await apiClient.get(`/events/${id}`);
    return response.data;
}

export async function createEvent(data: Partial<RetreatEvent>): Promise<RetreatEvent> {
    const response = await apiClient.post('/events', data);
    return response.data;
}

export async function updateEvent(id: number, data: Partial<RetreatEvent>): Promise<RetreatEvent> {
    const response = await apiClient.put(`/events/${id}`, data);
    return response.data;
}

export async function deleteEvent(id: number): Promise<void> {
    await apiClient.delete(`/events/${id}`);
}

export async function copyPricesFromEvent(targetId: number, sourceId: number): Promise<void> {
    await apiClient.post(`/events/${targetId}/prices/copy-from/${sourceId}`);
}

export interface CloneOptions {
    location: boolean;
    mealConfig: boolean;
    earlyBirdConfig: boolean;
    pricingRules: boolean;
    prices: boolean;
    accommodations: boolean;
    venuePrices: boolean;
    subEvents: boolean;
}

export interface ClonePreview {
    units: number;
    rooms: number;
    prices: number;
    venuePrices: number;
    venueExtras: number;
    subEvents: number;
}

export async function getClonePreview(sourceId: number): Promise<ClonePreview> {
    const response = await apiClient.get(`/events/${sourceId}/clone-preview`);
    return response.data;
}

export async function cloneEvent(
    sourceId: number,
    target: { name: string; startDate: string; endDate: string; earlyBirdDeadline?: string | null },
    copy: CloneOptions
): Promise<RetreatEvent> {
    const response = await apiClient.post('/events/clone', { sourceId, target, copy });
    return response.data;
}

// Registrations API
export interface GroupedRegistrationParticipant extends RetreatParticipant {
    participationId?: number;
    needsBedLinens?: boolean;
    needsBed?: boolean;
    availableSeats?: number;
    rideStatus?: 'SEARCHING' | 'OFFERING' | 'NONE';
    calculatedPrice?: number;
    customPriceOverride?: number | null;
    dietaryOption?: string;
    dailyPlans?: import('../types').DailyPlan[];
}

export interface GroupedRegistration {
    id: number;
    registrant: GroupedRegistrationParticipant;
    partner: GroupedRegistrationParticipant | null;
    children: GroupedRegistrationParticipant[];
    familyName: string | null;
    customGroupName: string | null;
    personCount: number;
    totalPrice: number;
    totalPaid: number;
    openBalance: number;
    installmentsAgreed: boolean;
    vhCoverageApproved: boolean;
    vhCoverageAmount: number;
    donationAmount: number;
    payments: Array<{ id: number; amount: number; paymentDate: string; note: string | null }>;
    status: 'PENDING' | 'CONFIRMED' | 'CANCELLED';
    hasPaid: boolean;
    registeredAt: string;
    notes: string | null;
    familyId?: number;
    coupleId?: number | null;
    needsQuiet?: boolean;
    needsAccessible?: boolean;
    roomPreference?: 'HOUSE' | 'BUNGALOW' | 'TENT' | 'OTHER';
    rideStatus?: 'SEARCHING' | 'OFFERING' | 'NONE';
    availableSeats?: number;
    viaManualAdmin?: boolean;

    // Manual Price Override (Group Level)
    isManualOverride?: boolean;
    manualTotalOverride?: number | null;
    manualOverrideReason?: string | null;
    systemSuggestedPrice?: number;
    standardPrice?: number;
}

export async function getGroupedRegistrations(eventId: number, includePending?: boolean): Promise<GroupedRegistration[]> {
    const response = await apiClient.get(`/registrations/grouped/${eventId}`, {
        params: { includePending }
    });
    return response.data;
}

export function getGroupName(reg: GroupedRegistration): string {
    // 1. Admin-set custom override takes top priority
    if (reg.customGroupName) return reg.customGroupName;

    // 2. familyName from backend (already dynamic from current participant data)
    if (reg.familyName) return reg.familyName;

    // 3. Compute locally as fallback (should not normally be reached)
    const registrant = reg.registrant;
    const partner = reg.partner;

    const rFirst = registrant.firstName.trim();
    const rLast = registrant.lastName.trim();

    if (partner) {
        const pFirst = partner.firstName.trim();
        const pLast = partner.lastName.trim();

        if (rLast.toLowerCase() === pLast.toLowerCase()) {
            return `${rFirst.charAt(0)}. + ${pFirst.charAt(0)}. ${rLast}`;
        } else {
            // Different last names
            return `${rFirst.charAt(0)}. ${rLast} & ${pFirst.charAt(0)}. ${pLast}`;
        }
    }

    // Single person: "A. Kraus"
    return `${rFirst.charAt(0)}. ${rLast}`;
}

export async function getRegistrations(params?: { status?: string; eventId?: number }): Promise<RegistrationWithDetails[]> {
    const response = await apiClient.get('/registrations', { params });
    return response.data;
}

export async function confirmRegistration(id: number): Promise<void> {
    await apiClient.post(`/registrations/${id}/confirm`);
}

export async function cancelRegistration(id: number): Promise<void> {
    await apiClient.post(`/registrations/${id}/cancel`);
}

export interface UpdateRegistrationPayload {
    notes?: string | null;
    needsBedLinens?: boolean;
    needsBed?: boolean;
    roomPreference?: 'HOUSE' | 'BUNGALOW' | 'TENT' | 'OTHER' | null;
    rideStatus?: 'SEARCHING' | 'OFFERING' | 'NONE';
    availableSeats?: number;
    isManualOverride?: boolean;
    manualTotalOverride?: number | null;
    manualOverrideReason?: string | null;
    viaManualAdmin?: boolean;
    customGroupName?: string | null;
    dietaryOption?: string | null;
}

export async function updateRegistration(id: number, data: UpdateRegistrationPayload): Promise<void> {
    await apiClient.put(`/registrations/${id}`, data);
}

export async function updateParticipantAttendance(id: number, plans: any[]): Promise<void> {
    await apiClient.put(`/registrations/${id}/attendance`, { plans });
}

export async function deleteRegistration(id: number): Promise<void> {
    await apiClient.delete(`/registrations/${id}`);
}

export async function createManualRegistration(data: import('../types').ManualRegistrationDTO): Promise<any> {
    const response = await apiClient.post('/registrations/manual', data);
    return response.data;
}

// Participants API
export async function getParticipants(params?: { eventId?: number; search?: string }): Promise<RetreatParticipant[]> {
    const response = await apiClient.get('/participants', { params });
    // Backend returns wrapped object { participant: ... }
    return response.data.map((item: any) => item.participant);
}

export async function updateParticipant(id: number, data: Partial<RetreatParticipant>): Promise<void> {
    await apiClient.put(`/participants/${id}`, data);
}

export async function updateFamily(id: number, data: { needsQuiet?: boolean; needsAccessible?: boolean }): Promise<void> {
    await apiClient.put(`/registrations/families/${id}`, data);
}

export async function getRooms(eventId: number): Promise<RetreatUnit[]> {
    const response = await apiClient.get(`/rooms/${eventId}`);
    return response.data;
}

export interface RetreatRoom {
    id: number;
    unitId: number;
    unitName?: string; // Building/Unit name for display
    name: string;
    capacity: number;
    isQuiet: boolean;
    isAccessible: boolean;
    bedType?: 'single' | 'double' | 'bunk' | 'mix';
    purpose: 'ACCOMMODATION' | 'VENUE' | 'BOTH';
    venueCapacity?: number | null;
}

export interface RetreatUnit {
    id: number;
    eventId: number;
    name: string;
    type: 'HOUSE' | 'BUNGALOW' | 'TENT' | 'OTHER';
    isQuiet: boolean;
    isAccessible: boolean;
    rooms: RetreatRoom[];
}

export interface RetreatAssignment {
    roomId: number;
    participationId: number;
    date: string;
}

export async function getAssignments(eventId: number): Promise<RetreatAssignment[]> {
    const response = await apiClient.get(`/assignments/${eventId}`);
    return response.data;
}

export async function assignParticipants(participationIds: number[], roomId: number) {
    const response = await apiClient.post('/assignments', { participationIds, roomId });
    return response.data;
}

export async function unassignParticipant(participationId: number) {
    await apiClient.delete(`/assignments/${participationId}`);
}

export async function autoAssignParticipants(eventId: number) {
    const response = await apiClient.post(`/assignments/auto/${eventId}`);
    return response.data;
}

export async function clearAssignments(eventId: number) {
    await apiClient.delete(`/assignments/event/${eventId}`);
}

export async function exportParticipantsCsv(eventId: number) {
    const response = await apiClient.get(`/exports/${eventId}/participants/csv`, {
        responseType: 'blob'
    });

    // Create download link from blob
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `participants_event_${eventId}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export async function getPrices(eventId: number): Promise<RetreatPriceCategory[]> {
    const response = await apiClient.get(`/prices/${eventId}`);
    return response.data;
}

export async function savePrices(eventId: number, prices: Partial<RetreatPriceCategory>[]): Promise<RetreatPriceCategory[]> {
    const response = await apiClient.post(`/prices/${eventId}`, prices);
    return response.data;
}

export interface RetreatPriceCategory {
    id: number;
    eventId: number;
    category: 'ADULT' | 'COUPLE' | 'STUDENT' | 'CHILD_UNDER_3' | 'CHILD_3_6' | 'CHILD_7_13' | 'CHILD_14_17';
    normalPrice: number;
    earlyBirdPrice: number | null;
    familyCap: number | null;
}

export async function createUnit(data: { eventId: number; name: string; type: string; isAccessible?: boolean; isQuiet?: boolean }) {
    const response = await apiClient.post('/rooms/units', data);
    return response.data;
}

export const RoomService = {
    getAll: async (eventId: number) => {
        const response = await apiClient.get(`/rooms/${eventId}`);
        return response.data;
    },

    create: async (data: any) => {
        const response = await apiClient.post('/rooms', data);
        return response.data;
    },

    update: async (id: number, data: any) => {
        const response = await apiClient.put(`/rooms/${id}`, data);
        return response.data;
    },

    delete: async (id: number) => {
        await apiClient.delete(`/rooms/${id}`);
    },

    autoAssign: async (eventId: number) => {
        const response = await apiClient.post(`/rooms/auto-assign/${eventId}`);
        return response.data;
    },

    updateAssignment: async (roomId: number, assignments: any[]) => {
        const response = await apiClient.put(`/rooms/${roomId}/assignments`, { assignments });
        return response.data;
    },

    getByPurpose: async (eventId: number, purpose: 'ACCOMMODATION' | 'VENUE'): Promise<RetreatRoom[]> => {
        const response = await apiClient.get(`/rooms/${eventId}?purpose=${purpose}`);
        // Backend returns Units with nested Rooms - flatten to room list and filter by purpose
        const units = response.data as { name: string; rooms: RetreatRoom[] }[];
        return units.flatMap(unit =>
            (unit.rooms || []).filter(room =>
                room.purpose === purpose || room.purpose === 'BOTH'
            ).map(room => ({ ...room, unitName: unit.name }))
        );
    }
};

export const SubEventService = {
    getAll: async (eventId: number): Promise<SubEvent[]> => {
        const response = await apiClient.get(`/subevents/${eventId}`);
        return response.data;
    },

    create: async (data: CreateSubEventDTO): Promise<SubEvent> => {
        const response = await apiClient.post('/subevents', data);
        return response.data;
    },

    update: async (id: number, data: UpdateSubEventDTO): Promise<SubEvent> => {
        const response = await apiClient.put(`/subevents/${id}`, data);
        return response.data;
    },

    delete: async (id: number): Promise<void> => {
        await apiClient.delete(`/subevents/${id}`);
    },

    checkConflicts: async (data: ConflictCheckDTO) => {
        const response = await apiClient.post('/subevents/check-conflicts', data);
        return response.data;
    },

    getRoleTypes: async (): Promise<{ id: number; name: string }[]> => {
        const response = await apiClient.get('/subevents/role-types/all');
        return response.data;
    },

    getTypes: async (): Promise<SubEventType[]> => {
        const response = await apiClient.get('/subevents/types/all');
        return response.data;
    },

    createType: async (data: CreateTypeDTO): Promise<SubEventType> => {
        const response = await apiClient.post('/subevents/types', data);
        return response.data;
    },

    updateType: async (id: number, data: { color?: string; name?: string }): Promise<SubEventType> => {
        const response = await apiClient.put(`/subevents/types/${id}`, data);
        return response.data;
    },

    deleteType: async (id: number): Promise<void> => {
        await apiClient.delete(`/subevents/types/${id}`);
    },

    createRoleType: async (name: string): Promise<{ id: number; name: string }> => {
        const response = await apiClient.post('/subevents/role-types', { name });
        return response.data;
    },

    duplicate: async (id: number): Promise<SubEvent> => {
        const response = await apiClient.post(`/subevents/${id}/duplicate`);
        return response.data;
    },

    getDetail: async (id: number): Promise<SubEvent> => {
        const response = await apiClient.get(`/subevents/detail/${id}`);
        return response.data;
    }
};

export async function createRoom(data: { unitId: number; name: string; capacity: number; isQuiet?: boolean; isAccessible?: boolean; bedType?: string; purpose?: 'ACCOMMODATION' | 'VENUE' | 'BOTH'; venueCapacity?: number }) {
    const response = await apiClient.post('/rooms', data);
    return response.data;
}

export async function deleteUnit(id: number) {
    await apiClient.delete(`/rooms/units/${id}`);
}

export async function deleteRoom(id: number) {
    await apiClient.delete(`/rooms/${id}`);
}

export async function duplicateUnit(id: number) {
    const response = await apiClient.post(`/rooms/units/${id}/duplicate`);
    return response.data;
}

export async function duplicateRoom(id: number) {
    const response = await apiClient.post(`/rooms/${id}/duplicate`);
    return response.data;
}

export async function updateUnit(id: number, data: { name?: string; type?: string; isAccessible?: boolean; isQuiet?: boolean }) {
    const response = await apiClient.put(`/rooms/units/${id}`, data);
    return response.data;
}

export async function updateRoom(id: number, data: { name?: string; capacity?: number; isQuiet?: boolean; isAccessible?: boolean; bedType?: string; purpose?: 'ACCOMMODATION' | 'VENUE' | 'BOTH'; venueCapacity?: number | null }) {
    const response = await apiClient.put(`/rooms/${id}`, data);
    return response.data;
}


// Finance API
export interface FinanceSummary {
    id: number;
    totalPrice: number;
    totalPaid: number;
    openBalance: number;
    adjustmentAmount: number;
    groupName: string;
    breakdown: Array<{
        name: string;
        role: string;
        price: number;
        category: string;
        details: string;
    }>;
    installmentsAgreed: boolean;
    vhCoverageApproved: boolean;
    vhCoverageAmount: number;
    donationAmount: number;
    payments: Array<{
        id: number;
        amount: number;
        paymentDate: string;
        note: string | null;
    }>;
}

export async function getFinanceSummary(participationId: number): Promise<FinanceSummary> {
    const response = await apiClient.get(`/registrations/${participationId}/finance`);
    return response.data;
}

export async function addPayment(participationId: number, data: { amount: number; date: string; note?: string }): Promise<void> {
    await apiClient.post(`/registrations/${participationId}/payments`, data);
}

export async function updatePayment(paymentId: number, data: { amount: number; date: string; note?: string }): Promise<void> {
    await apiClient.put(`/registrations/payments/${paymentId}`, data);
}

export async function deletePayment(paymentId: number): Promise<void> {
    await apiClient.delete(`/registrations/payments/${paymentId}`);
}

export async function updateFinancialStatus(participationId: number, data: { installmentsAgreed?: boolean; vhCoverageApproved?: boolean; vhCoverageAmount?: number; donationAmount?: number }): Promise<void> {
    await apiClient.put(`/registrations/${participationId}/financial-status`, data);
}

export async function exportFinanceCsv(eventId: number) {
    const response = await apiClient.get(`/exports/${eventId}/finance/csv`, {
        responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `finance_event_${eventId}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export async function exportRoomsPdf(eventId: number) {
    const response = await apiClient.get(`/exports/${eventId}/rooms/pdf`, {
        responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `zimmer_event_${eventId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export async function exportFinancePdf(eventId: number) {
    const response = await apiClient.get(`/exports/${eventId}/finance/pdf`, {
        responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // For PDF we might want to open in new tab instead of download, but user asked for export.
    // Let's do download for now as it's an "Export" action.
    link.setAttribute('download', `finance_event_${eventId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export async function exportParticipantFinancePdf(participationId: number) {
    const response = await apiClient.get(`/exports/participation/${participationId}/finance/pdf`, {
        responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `finanzaufstellung_anmeldung_${participationId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export async function exportSubEventsPdf(eventId: number) {
    const response = await apiClient.get(`/exports/${eventId}/subevents/pdf`, {
        responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `programm_event_${eventId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export const CheckInService = {
    getOverview: async (eventId: number): Promise<CheckInEntry[]> => {
        const response = await apiClient.get(`/checkin/${eventId}/overview`);
        return response.data;
    },
    initialize: async (eventId: number): Promise<{ success: boolean }> => {
        const response = await apiClient.post(`/checkin/${eventId}/initialize`);
        return response.data;
    },
    checkIn: async (id: number): Promise<CheckInEntry> => {
        const response = await apiClient.put(`/checkin/${id}/check-in`);
        return response.data;
    },
    checkOut: async (id: number): Promise<CheckInEntry & { warning?: string }> => {
        const response = await apiClient.put(`/checkin/${id}/check-out`);
        return response.data;
    },
    toggleAttendance: async (id: number, isPresent: boolean): Promise<AttendanceEntry> => {
        const response = await apiClient.put(`/checkin/attendance/${id}`, { isPresent });
        return response.data;
    },
    updateInfoPacket: async (id: number, data: { issued: boolean; note?: string }): Promise<CheckInEntry> => {
        const response = await apiClient.put(`/checkin/${id}/info-packet`, data);
        return response.data;
    },
    updateMealBand: async (id: number, issued: boolean): Promise<CheckInEntry> => {
        const response = await apiClient.put(`/checkin/${id}/meal-band`, { issued });
        return response.data;
    },
    reset: async (id: number): Promise<CheckInEntry> => {
        const response = await apiClient.put(`/checkin/${id}/reset`);
        return response.data;
    },
    issueKeys: async (data: any): Promise<KeyHandout> => {
        const response = await apiClient.post('/keys/issue', data);
        return response.data;
    },
    returnKeys: async (id: number, count: number): Promise<KeyHandout> => {
        const response = await apiClient.put(`/keys/${id}/return`, { count });
        return response.data;
    }
};

export const KeyService = {
    getOverview: async (eventId: number): Promise<{ accommodations: KeyDashboardItem[], community: KeyDashboardItem[] }> => {
        const response = await apiClient.get(`/keys/${eventId}`);
        return response.data;
    },
    getCommunityRooms: async (eventId: number): Promise<any[]> => {
        const response = await apiClient.get(`/keys/${eventId}/community`);
        return response.data;
    },
    getWarnings: async (eventId: number): Promise<any[]> => {
        const response = await apiClient.get(`/keys/${eventId}/warnings`);
        return response.data;
    },
    issueKeys: async (data: any): Promise<KeyHandout> => {
        const response = await apiClient.post('/keys/issue', data);
        return response.data;
    },
    returnKeys: async (id: number, count: number): Promise<KeyHandout> => {
        const response = await apiClient.put(`/keys/${id}/return`, { count });
        return response.data;
    },
    resetEventKeys: async (eventId: number): Promise<void> => {
        const response = await apiClient.delete(`/keys/${eventId}/reset`);
        return response.data;
    },
};

export const DefectService = {
    getAll: async (eventId: number, status?: string): Promise<Defect[]> => {
        const response = await apiClient.get(`/defects/${eventId}`, { params: { status } });
        return response.data;
    },
    create: async (data: any): Promise<Defect> => {
        const response = await apiClient.post('/defects', data);
        return response.data;
    },
    update: async (id: number, data: any): Promise<Defect> => {
        const response = await apiClient.put(`/defects/${id}`, data);
        return response.data;
    },
    updateStatus: async (id: number, status: string): Promise<Defect> => {
        const response = await apiClient.put(`/defects/${id}/status`, { status });
        return response.data;
    },
    resetEventDefects: async (eventId: number): Promise<void> => {
        const response = await apiClient.delete(`/defects/${eventId}/reset`);
        return response.data;
    },
};

export async function exportFilteredParticipantsPdf(eventId: number, participantIds: number[], filterDescription: string) {
    const response = await apiClient.post(`/exports/${eventId}/participants/pdf`, {
        participantIds,
        filterDescription
    }, {
        responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `participants_filtered_${eventId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

// Helper to save entire group structure
export async function saveGroupedRegistration(updatedReg: GroupedRegistration): Promise<any[]> {
    const promises: Promise<any>[] = [];

    // 1. Update Registrant
    promises.push(updateParticipant(updatedReg.registrant.id, {
        firstName: updatedReg.registrant.firstName,
        lastName: updatedReg.registrant.lastName,
        birthDate: updatedReg.registrant.birthDate,
        dietaryOption: updatedReg.registrant.dietaryOption,
        isDiscounted: updatedReg.registrant.isDiscounted
    }));

    // 1b. Update Registrant Participation
    promises.push(updateRegistration(updatedReg.id, {
        notes: updatedReg.notes,
        needsBedLinens: updatedReg.registrant.needsBedLinens,
        rideStatus: updatedReg.registrant.rideStatus,
        availableSeats: updatedReg.registrant.availableSeats,
        roomPreference: updatedReg.roomPreference,
        isManualOverride: updatedReg.isManualOverride,
        manualTotalOverride: updatedReg.manualTotalOverride ?? undefined,
        manualOverrideReason: updatedReg.manualOverrideReason ?? undefined,
        customGroupName: updatedReg.customGroupName,
        dietaryOption: updatedReg.registrant.dietaryOption
    }));

    // 2. Update Partner
    if (updatedReg.partner) {
        promises.push(updateParticipant(updatedReg.partner.id, {
            firstName: updatedReg.partner.firstName,
            lastName: updatedReg.partner.lastName,
            birthDate: updatedReg.partner.birthDate,
            dietaryOption: updatedReg.partner.dietaryOption,
            isDiscounted: updatedReg.partner.isDiscounted
        }));

        if (updatedReg.partner.participationId) {
            promises.push(updateRegistration(updatedReg.partner.participationId, {
                needsBedLinens: updatedReg.partner.needsBedLinens,
                rideStatus: updatedReg.partner.rideStatus,
                availableSeats: updatedReg.partner.availableSeats,
                dietaryOption: updatedReg.partner.dietaryOption
            }));
        }
    }

    // 3. Update Children
    for (const child of updatedReg.children) {
        promises.push(updateParticipant(child.id, {
            firstName: child.firstName,
            lastName: child.lastName,
            birthDate: child.birthDate,
            dietaryOption: child.dietaryOption,
            isDiscounted: child.isDiscounted
        }));
        if (child.participationId) {
            promises.push(updateRegistration(child.participationId, {
                needsBed: child.needsBed,
                needsBedLinens: child.needsBedLinens,
                rideStatus: child.rideStatus,
                availableSeats: child.availableSeats,
                dietaryOption: child.dietaryOption
            }));
        }
    }

    // 4. Update Family Flags
    if (updatedReg.familyId) {
        promises.push(updateFamily(updatedReg.familyId, {
            needsQuiet: updatedReg.needsQuiet,
            needsAccessible: updatedReg.needsAccessible
        }));
    }

    return Promise.all(promises);
}

export const BillingService = {
    getVenuePrices: async (eventId: number): Promise<VenuePrice[]> => {
        const response = await apiClient.get(`/venue-prices/${eventId}`);
        return response.data;
    },
    saveVenuePrices: async (eventId: number, prices: VenuePrice[]): Promise<void> => {
        await apiClient.post(`/venue-prices/${eventId}`, prices);
    },
    getVenueExtras: async (eventId: number): Promise<VenueExtra[]> => {
        const response = await apiClient.get(`/venue-extras/${eventId}`);
        return response.data;
    },
    createVenueExtra: async (eventId: number, data: Partial<VenueExtra>): Promise<VenueExtra> => {
        const response = await apiClient.post(`/venue-extras/${eventId}`, data);
        return response.data;
    },
    updateVenueExtra: async (id: number, data: Partial<VenueExtra>): Promise<VenueExtra> => {
        const response = await apiClient.put(`/venue-extras/${id}`, data);
        return response.data;
    },
    deleteVenueExtra: async (id: number): Promise<void> => {
        await apiClient.delete(`/venue-extras/${id}`);
    },
    calculate: async (eventId: number): Promise<CalculationResult> => {
        const response = await apiClient.get(`/billing/${eventId}/calculate`);
        return response.data;
    },
    getStatistics: async (eventId: number): Promise<import('../types').EventStatistics> => {
        const response = await apiClient.get(`/billing/${eventId}/statistics`);
        return response.data;
    },
    exportStatisticsPdf: async (eventId: number): Promise<void> => {
        const response = await apiClient.get(`/billing/${eventId}/statistics/pdf`, {
            responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `statistik_event_${eventId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }
};

export interface CarpoolParticipant {
    participationId: number;
    firstName: string;
    lastName: string;
    personCount: number;
    rideStatus: 'SEARCHING' | 'OFFERING';
    availableSeats: number;
    // Helper fields
    familyName?: string;
    isDriver?: boolean;
}

export interface CarpoolAssignment {
    id: number;
    seekerParticipationId: number;
    driverParticipationId: number;
}

export interface FleetVehicle {
    participationId: number;
    driverName: string;
    capacity: number;
    assignedCount: number;
    seatsFree: number;
    passengers: {
        assignmentId: number;
        participationId: number;
        name: string;
        personCount: number;
    }[];
}

export interface CarpoolOverview {
    seekers: CarpoolParticipant[];
    drivers: CarpoolParticipant[];
    assignments: CarpoolAssignment[];
    fleet: FleetVehicle[];
    waitingList: CarpoolParticipant[];
}

export const CarpoolService = {
    getOverview: async (eventId: number): Promise<CarpoolOverview> => {
        const response = await apiClient.get(`/carpool/${eventId}/overview`);
        return response.data;
    },

    assign: async (seekerParticipationId: number, driverParticipationId: number, eventId: number) => {
        const response = await apiClient.post('/carpool/assign', { seekerParticipationId, driverParticipationId, eventId });
        return response.data;
    },

    unassign: async (assignmentId: number) => {
        const response = await apiClient.delete(`/carpool/assign/${assignmentId}`);
        return response.data;
    },
};
